import React, { useEffect, useState } from 'react';
import { TabBar, SkeletonHero, SkeletonCard, SectionError } from '../intel/ui';
import LeagueHeader from '../intel/LeagueHeader';
import LeagueOverview from '../intel/LeagueOverview';
import LeagueTeams from '../intel/LeagueTeams';
import LeagueMatchweeks from '../intel/LeagueMatchweeks';
import LeagueTopScorers from '../intel/LeagueTopScorers';
import StandingsTable from '../intel/StandingsTable';

const TABS = ['Overview', 'Standings', 'Teams', 'Matchweeks', 'Top Scorers'];
const EP = (id) => `https://stats.interaios.com/api/v1/leagues-sportsmonk/get-league/${id}`;

function extractId(entity) {
  const raw = entity?.raw;
  return raw?.league_id ?? raw?.id ?? raw?.league?.id ?? null;
}

export default function LeagueDashboard({ entity }) {
  const id = extractId(entity);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(EP(id));
        if (!res.ok) throw new Error('League data unavailable');
        const json = await res.json();
        if (!json?.data?.detail && !json?.data?.standings && !json?.data?.teams) throw new Error('League data unavailable');
        if (!cancelled) setData(json.data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, retryKey]);

  return (
    <div className="entity-dashboard intel" style={{ padding: '20px 24px 24px' }}>
      {!id && !loading && (
        <div className="i-card p-5">
          <h3 className="i-fg font-semibold">{entity?.name || 'League'}</h3>
          <p className="i-dim text-sm mt-1">No league details available for this entity.</p>
        </div>
      )}
      {loading && (<><SkeletonHero />{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</>)}
      {error && <SectionError message={error} onRetry={() => setRetryKey((k) => k + 1)} />}
      {data && !loading && (
        <>
          <LeagueHeader data={data} />
          <div className="i-card p-0 mb-4">
            <TabBar tabs={TABS} active={tab} onChange={setTab} />
            <div className="p-4">
              {tab === 'Overview' && <LeagueOverview data={data} />}
              {tab === 'Standings' && (
                data?.standings?.length
                  ? <StandingsTable rows={data.standings} />
                  : <p className="i-dim text-sm">No standings available.</p>
              )}
              {tab === 'Teams' && <LeagueTeams data={data} />}
              {tab === 'Matchweeks' && <LeagueMatchweeks data={data} />}
              {tab === 'Top Scorers' && <LeagueTopScorers data={data} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
